'use client';

import { matchesPublicCategoryProductsSeed, type PublicCategoryProductsSeed } from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { CollectionPageSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const isInitialized = useCustomerStore(state => state.isInitialized);

  if (!isInitialized && !props.initialProducts) return <CategoryProductsSkeleton />;

  return <CategoryProductsContent {...props} />;
}

function CategoryProductsContent(props: CategoryProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filters = useCategoryStore(s => s.filter);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const currentPage = searchParams.get('page');

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const queryWithoutPage = useMemo(
    () =>
      buildProductsQuery({
        attributes: filters.attributes,
        categories: [props.category.id],
        showCategories: true,
        manufacturers: filters.manufacturers ?? [],
        results: generalLayout?.pagination?.paginationProducts || 12,
        order: filters.sort.order,
        orderBy: filters.sort.orderBy,
        // values: filters.attributes.flatMap(attr => attr.values),
        price_from: filters.price_min,
        price_to: filters.price_max,
        availabilities: filters.availabilities,
        suppliers: filters.suppliers,
      }),
    [
      props.category.id,
      filters.manufacturers,
      generalLayout?.pagination?.paginationProducts,
      filters.sort.order,
      filters.sort.orderBy,
      filters.price_min,
      filters.price_max,
      filters.availabilities,
      filters.suppliers,
      filters.attributes,
    ],
  );

  const productRequestUri = buildProductsRequest({
    ...queryWithoutPage,
    page: currentPage ? parseInt(currentPage) : 1,
  }).uri;
  const productResetUri = buildProductsRequest({
    ...{
      ...queryWithoutPage,
      page: currentPage ? parseInt(currentPage) : 1,
    },
    page: undefined,
  }).uri;

  const productQueryKey = [
    CacheKey.PRODUCTS,
    'infinite',
    currentPage,
    productRequestUri,
    authToken,
    locale,
    filters.attributes,
  ];

  const productsQuery = useSuspenseInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(props.initialProducts, {
      locale: locale, requestUri: productRequestUri, authToken, page: Number(currentPage ?? 1),
    }) ? { pages: [props.initialProducts!.data], pageParams: [Number(currentPage ?? 1)] } : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    queryKey: productQueryKey,
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];
  const [loadMode, setLoadMode] = useState<'manual' | 'auto'>('manual');
  const [fetchTrigger, setFetchTrigger] = useState<'load-more' | 'show-all' | 'auto' | null>(null);
  const autoLoadRef = useRef<HTMLDivElement | null>(null);

  const fetchNextPageSafely = useCallback(
    async (trigger: 'load-more' | 'show-all' | 'auto') => {
      if (!productsQuery.hasNextPage) return false;
      if (productsQuery.isFetchingNextPage) return false;
      setFetchTrigger(trigger);
      try {
        await productsQuery.fetchNextPage();
        return true;
      } finally {
        setFetchTrigger(null);
      }
    },
    [productsQuery],
  );

  useEffect(() => {
    if (loadMode !== 'auto') return;
    if (!productsQuery.hasNextPage) return;

    const target = autoLoadRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void fetchNextPageSafely('auto');
      },
      { root: null, rootMargin: '250px 0px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPageSafely, loadMode, productsQuery.hasNextPage]);

  useEffect(() => {
    setLoadMode('manual');
    setFetchTrigger(null);
  }, [currentPage, locale, authToken, queryWithoutPage, filters.attributes]);

  useEffect(() => {
    props.onProductTotalChange?.(lastPage?.meta?.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  return (
    <div
      className={cn('relative col-span-12 flex flex-col gap-6 @container', 'ea-category-products', {
        'col-span-12': !templateSettings.category?.left_filter?.enabled,
      })}
    >
      {!flatList?.length && <p className="text-center text-muted-foreground">{t('text554')}</p>}

      {!!flatList.length && (
        <CollectionPageSchema
          category={props.category}
          products={flatList}
          order={filters.sort.order ?? 'desc'}
          url={props.category.breadcrumb?.slug || props.category.seo?.['og:url']}
          currency={(generalLayout?.currency_code || generalLayout?.currency)}
        />
      )}

      <ProductsGrid
        products={flatList ?? []}
        emitSchema={false}
        className={{
          wrapper: cn(
            'grid grid-cols-2  @lg:grid-cols-2 @2xl:grid-cols-3',
            'ea-category-products__grid',
            props.className?.grid_wrapper,
          ),
          product: {
            main: cn(props.className?.product),
          },
        }}
      />

      {loadMode === 'manual' && !!productsQuery.hasNextPage && (
        <div
          className={cn(
            'flex flex-col justify-center gap-2 md:flex-row',
            'ea-category-products__load-more',
          )}
        >
          <Button
            variant="secondary"
            isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'load-more'}
            onClick={() => void fetchNextPageSafely('load-more')}
          >
            {t('text338')}
          </Button>
          <Button
            variant="secondary"
            isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'show-all'}
            onClick={async () => {
              const fetched = await fetchNextPageSafely('show-all');
              if (fetched) setLoadMode('auto');
            }}
          >
            {t('text3161')}
          </Button>
        </div>
      )}

      {loadMode === 'auto' && !!productsQuery.hasNextPage && (
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-2',
            'ea-category-products__auto-load',
          )}
        >
          <div ref={autoLoadRef} className="h-1 w-full" />
          {!!productsQuery.isFetchingNextPage && (
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
          )}
        </div>
      )}

      <div className={cn('flex flex-row justify-center', 'ea-category-products__pagination')}>
        <Pagination
          links={lastPage?.meta.links}
          onPageClicked={page => {
            if (String(page) !== String(currentPage)) return;
            queryClient.resetQueries({
              queryKey: productQueryKey,
              exact: true,
            });
          }}
          scrollToElementAfterPageChange={
            props.paginationProps?.scrollToElementAfterPageChange || undefined
          }
          resetWhen={[productResetUri]}
        />
      </div>

      {!!isGlobalNavigating && (
        <div
          className={cn(
            'absolute inset-0 z-10 bg-background/30',
            'ea-category-products__loading-overlay',
          )}
        ></div>
      )}
    </div>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    container?: string;
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative col-span-12 flex flex-col gap-4 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
        className?.container,
      )}
    >
      <div
        className={cn(
          'grid gap-4 @sm:grid-cols-1 @lg:grid-cols-2 @2xl:grid-cols-3',
          className?.items_wrapper,
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className={cn('min-h-[400px] min-w-[250px]', className?.item)} />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
